import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAudio } from "@/contexts/audio-context";
import { cn } from "@/lib/utils";
import { PhaseType } from "@/types/voice/call_analytics";
import { Pause, Play } from "lucide-react";
import { ForwardedRef, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";

interface AudioPlayerProps {
    audioUrl: string;
    className?: string;
    phases: PhaseType[] | undefined | null;
    onTimeUpdate?: (time: number) => void;
}

export const AudioPlayer2 = forwardRef(function AudioPlayer2({ audioUrl, className, onTimeUpdate }: AudioPlayerProps, ref: ForwardedRef<HTMLAudioElement>) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const { setCurrentTime, currentTime } = useAudio();

    useImperativeHandle(ref, () => audioRef.current as HTMLAudioElement);

    const togglePlayPause = useCallback(() => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play().catch(error => {
                    console.error("Error playing audio:", error);
                });
            }
            setIsPlaying(!isPlaying);
        }
    }, [isPlaying]);

    useEffect(() => {
        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
        };

        const handleTimeUpdate = () => {
            const currentTime = audio.currentTime;
            setProgress((currentTime / audio.duration) * 100);

            // Mettre à jour le contexte audio seulement si onTimeUpdate est fourni
            if (onTimeUpdate) {
                setCurrentTime(currentTime);
                onTimeUpdate(currentTime);
            }
        };

        const handleEnded = () => {
            setIsPlaying(false);
            setProgress(0);
        };

        audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);

        return () => {
            audio.pause();
            audio.src = "";
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
        };
    }, [audioUrl, onTimeUpdate]);

    // Gestion des touches clavier
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!audioRef.current) return;

            // Ignorer les touches si l'utilisateur est en train de taper dans un champ de formulaire
            const activeElement = document.activeElement;
            const isInputField =
                activeElement &&
                (activeElement.tagName === "INPUT" ||
                    activeElement.tagName === "TEXTAREA" ||
                    activeElement.tagName === "SELECT" ||
                    activeElement.hasAttribute("contenteditable") ||
                    activeElement.getAttribute("role") === "textbox" ||
                    activeElement.getAttribute("role") === "combobox");

            // Pour la barre d'espace, on ignore complètement si on est dans un champ de formulaire
            if (event.key === " " && isInputField) {
                return;
            }

            // Pour les flèches, on ignore aussi si on est dans un champ de formulaire
            if ((event.key === "ArrowLeft" || event.key === "ArrowRight") && isInputField) {
                return;
            }

            switch (event.key) {
                case "ArrowLeft":
                    event.preventDefault();
                    const newTimeBackward = Math.max(0, audioRef.current.currentTime - 10);
                    audioRef.current.currentTime = newTimeBackward;
                    setCurrentTime(newTimeBackward);
                    if (onTimeUpdate) {
                        onTimeUpdate(newTimeBackward);
                    }
                    break;
                case "ArrowRight":
                    event.preventDefault();
                    const newTimeForward = Math.min(duration, audioRef.current.currentTime + 10);
                    audioRef.current.currentTime = newTimeForward;
                    setCurrentTime(newTimeForward);
                    if (onTimeUpdate) {
                        onTimeUpdate(newTimeForward);
                    }
                    break;
                case " ":
                    event.preventDefault();
                    togglePlayPause();
                    break;
            }
        };

        // Ajouter l'écouteur d'événements au document
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [duration, onTimeUpdate, togglePlayPause, setCurrentTime]);

    // Surveiller le paramètre 't' dans l'URL pour naviguer automatiquement
    useEffect(() => {
        if (currentTime && audioRef.current) {
            const timestamp = currentTime;
            if (!isNaN(timestamp) && timestamp >= 0) {
                audioRef.current.currentTime = timestamp;
                setProgress((timestamp / duration) * 100);
                setCurrentTime(timestamp);

                if (onTimeUpdate) {
                    onTimeUpdate(timestamp);
                }
            }
        }
    }, [currentTime, duration, onTimeUpdate, setCurrentTime]);

    // const handlePhaseClick = (timestamp: number) => {
    //     if (audioRef.current) {
    //         audioRef.current.currentTime = timestamp;
    //     }
    // };

    const formatTime = (seconds: number) => {
        if (isNaN(seconds) || seconds === 0) {
            return "00:00";
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (audioRef.current) {
            const progressBar = e.currentTarget;
            const rect = progressBar.getBoundingClientRect();
            const clickPosition = e.clientX - rect.left;
            const percentageClicked = Math.max(0, Math.min(100, (clickPosition / rect.width) * 100));
            const newTime = (percentageClicked / 100) * duration;

            audioRef.current.currentTime = newTime;
            setProgress(percentageClicked);
            setCurrentTime(newTime); // Mettre à jour le contexte audio

            if (onTimeUpdate) {
                onTimeUpdate(newTime);
            }
        }
    };

    return (
        <div className={cn("flex w-full items-center gap-2", className)}>
            <div className="flex flex-shrink-0 items-center pb-4">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={togglePlayPause}
                    className="flex h-9 w-9 items-center justify-center rounded-full border-gray-200 bg-white p-0 shadow-sm hover:bg-gray-50"
                    aria-label={isPlaying ? "Pause" : "Play"}
                >
                    <div className="flex h-full w-full items-center justify-center">{isPlaying ? <Pause className="h-4 w-4 text-gray-700" /> : <Play className="ml-0.5 h-4 w-4 text-gray-700" />}</div>
                </Button>
            </div>

            <div className="flex flex-1 flex-col">
                <div className="mb-2 flex justify-between text-xs text-gray-500">
                    <span>{formatTime(audioRef.current?.currentTime || 0)}</span>
                    <span className="">{formatTime(duration)}</span>
                </div>
                <div
                    className="group relative mb-1 w-full cursor-pointer"
                    onClick={handleProgressClick}
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={progress}
                    aria-label="Audio progress"
                    tabIndex={0}
                >
                    <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                        <Progress value={progress} className="h-full transition-all" indicatorClassName="bg-primary group-hover:bg-primary/90" />
                    </div>
                </div>
                {/* <div className="relative h-8 w-full overflow-x-hidden rounded-md bg-[repeating-linear-gradient(-45deg,_#e5e7eb,_#e5e7eb_4px,_#f9fafb_4px,_#f9fafb_8px)]">
                    {phases &&
                        phases.map((phase, index) => {
                            if (!phase.name || phase.timestamp_start == null || phase.timestamp_end == null) return null;
                            const left = Math.max(0, Math.min(99.5, (phase.timestamp_start / duration) * 100));
                            let width = ((phase.timestamp_end - phase.timestamp_start) / duration) * 100;
                            if (left + width > 100) width = 100 - left;
                            width = Math.max(0, width);
                            if (left >= 100 || width <= 0) return null;
                            return (
                                <button
                                    key={index}
                                    className={`absolute h-full text-xs text-black transition
                                        ${index % 2 === 0 ? "bg-blue-100 hover:bg-blue-200" : "bg-blue-300 hover:bg-blue-400"}`}
                                    style={{
                                        left: `${left}%`,
                                        width: `${width}%`
                                    }}
                                    onClick={() => handlePhaseClick(phase.timestamp_start as number)}
                                >
                                    {phase.name.replace("de la formation", "")}
                                </button>
                            );
                        })}
                </div> */}
            </div>
        </div>
    );
});
